import { observer } from 'mobx-react';
import { makeAutoObservable } from 'mobx';
import { IUserConfigPackage } from '../../../API';
import { FieldType, IContentMetadataField, IEntityToken } from '../../../API';
import ReusableDialog, { IReusableDialogFooterProps, ReusableDialogState } from '../../controls/ReusableDialog';
import { InfoTooltipLabel } from '../../controls/InfoTooltipLabel';
import { Checkbox, ComboBox, Dropdown, MessageBar, Pivot, PivotItem, TextField } from '@fluentui/react';
import { useEffect } from 'react';
import TokenPathTagPicker, { TokenPathTagPickerState } from '../../controls/TokenPathTagPicker';

export class ContentMetadataFieldState implements IContentMetadataField {
	
	public userConfig: IUserConfigPackage;

	public dialog:ReusableDialogState;

	public Id: string = '';
	public ValueFromTokenizedValue: string = '';
	public DestinationFieldInternalName: string = '';
	public CreateFieldIfMissing: boolean = true;
	public CreateFieldCustomFieldType: FieldType = FieldType.Text;
	public CreateFieldDisplayName: string = '';

	//Unused
	//-------------
	public DisplayTitle: string = '';
	public ServerRelativeUrl: string = '';
	public NativeMetadataUrl: string = '';
	original: IContentMetadataField = {} as IContentMetadataField;

	//-------------
	
	tokens: IEntityToken[] = [];

	private thisItem() : IContentMetadataField {
		return {
			Id: this.Id,
			ValueFromTokenizedValue: this.ValueFromTokenizedValue,
			CreateFieldIfMissing: this.CreateFieldIfMissing,
			CreateFieldCustomFieldType: this.CreateFieldCustomFieldType,
			DestinationFieldInternalName: this.DestinationFieldInternalName,
			CreateFieldDisplayName: this.CreateFieldDisplayName,
			DisplayTitle: this.DisplayTitle,
			ServerRelativeUrl: this.ServerRelativeUrl,
			NativeMetadataUrl: this.NativeMetadataUrl,
		};
	}

	public dialogButtons: IReusableDialogFooterProps = {
		leftButtons: [/*
			{
				text: 'Delete',
				iconProps: {iconName: 'Delete'},
				onClick: (ev) => {
					this.dialog.onDismiss();
					this.onDelete(this.original);
				},
			}
		*/],
		rightButtons: [
			{
				text: 'Cancel',
				onClick: (ev) => {
					this.dialog.onDismiss();
				},
			},
			{
				text: 'Save',
				onClick: (ev) => {
					this.dialog.onDismiss();
					this.onSave(this.original, this.thisItem());
				},
			}
		],
	};

	onSave: (original:IContentMetadataField, updated:IContentMetadataField) => void;
	onDelete: (profile:IContentMetadataField) => void;

	constructor(
		userConfig: IUserConfigPackage,
		onDelete: (profile:IContentMetadataField) => void,
		onSave: (original:IContentMetadataField, updated:IContentMetadataField) => void
	) {
		this.userConfig = userConfig;
		this.onDelete = onDelete;
		this.onSave = onSave;
		
		this.dialog = new ReusableDialogState("Workspace Metadata Field", 500);
		
		makeAutoObservable(this);
	}

	public availableContentHubColumns: string[] = [];
	public setAvailableContentHubColumns(columns: string[]) {
		this.availableContentHubColumns = columns;

		if(this.availableContentHubColumns.indexOf(this.DestinationFieldInternalName) === -1) {
			this.availableContentHubColumns.push(this.DestinationFieldInternalName);
		}
	}
	
	public setTokens(tokens:IEntityToken[]) {
		this.tokens = tokens;
	}

	public setItem(item:IContentMetadataField) {
		this.original = item;
		Object.assign(this, item);
	}
	
	public setValueFromTokenizedValue(val:string) {
		this.ValueFromTokenizedValue = val;
	}

	public setCreateFieldIfMissing(val:boolean) {
		this.CreateFieldIfMissing = val;
	}

	public setCreateFieldCustomFieldType(val:FieldType) {
		this.CreateFieldCustomFieldType = val;
	}

	public setDestinationFieldInternalName(val:string) {
		this.DestinationFieldInternalName = val;

		if(this.availableContentHubColumns.indexOf(val) === -1) {
			this.availableContentHubColumns.push(val);
		}
	}

	public setCreateFieldDisplayName(val:string) {
		this.CreateFieldDisplayName = val;
	}

	public async ensureInitialLoaded() : Promise<void> {
		
	}
}

function ContentMetadataFieldEditor({
	state
}: {
	state: ContentMetadataFieldState
}) {

	useEffect(() => {
		state.ensureInitialLoaded();
	}, [state]);
	
	return (

		<ReusableDialog state={state.dialog} footerButtons={state.dialogButtons}>
			
			<Pivot>
				<PivotItem headerText='Source value'>

					<MessageBar>Select the value to be set on content documents. This could come from a specific list or lookup value, or be a fixed value.</MessageBar>

					<InfoTooltipLabel 
						label={`Value`}
						title='Construct your value from tokens and it will be generated dynamically based on your metadata.'>

						<TokenPathTagPicker
							state={new TokenPathTagPickerState(state.setValueFromTokenizedValue.bind(state), '', undefined, state.ValueFromTokenizedValue, state.tokens ?? [])}
						/>
					</InfoTooltipLabel>

				</PivotItem>
				<PivotItem headerText='Destination field'>

					<InfoTooltipLabel 
						label='Field internal name'
						title={`Enter the internal name for the field that will be created in SharePoint.`}>
						<ComboBox
							defaultSelectedKey={state.DestinationFieldInternalName}
							allowFreeform={true}
							onChange={(_, o, i, v) => {
								let opt = (v || o?.key || '').toString();
								state.setDestinationFieldInternalName(opt);
							}}
							options={state.availableContentHubColumns.map(c => ({ key: c, text: c }))}
						/>
					</InfoTooltipLabel>

					<InfoTooltipLabel 
						label='Create if missing'
						title={`If the field does not already exist in the target site, it will be created.`}>
						<Checkbox
							label="Allow creation of the field if missing"
							checked={state.CreateFieldIfMissing}
							onChange={(e, checked) => { state.setCreateFieldIfMissing(checked?.valueOf() ?? false); } }
						/>
					</InfoTooltipLabel>

				{ state.CreateFieldIfMissing ? <>
					<InfoTooltipLabel label='Field type' title='The field type to be used if creating a missing field.' >
						<Dropdown
							defaultSelectedKey={Number(state.CreateFieldCustomFieldType) || FieldType.Text}
							onChange={(_, o) => {
								state.setCreateFieldCustomFieldType(o?.key as FieldType);
							}}
							options={[
								{ key: FieldType.Text, text: 'Text' },
								{ key: FieldType.DateTime, text: 'Date' },
								{ key: FieldType.User, text: 'People' },
								{ key: FieldType.Boolean, text: 'Yes/No' },
								{ key: FieldType.Number, text: 'Number' }
							]}
						/>
					</InfoTooltipLabel>

					<InfoTooltipLabel 
						label='Field display name'
						title={`Enter the display name for the field that will be created in SharePoint.`}>
						<TextField
							defaultValue={state.CreateFieldDisplayName}
							onChange={(_, v) => state.setCreateFieldDisplayName(v || '')}
						/>
					</InfoTooltipLabel>
				</> : <></>}

			</PivotItem>
		</Pivot>
		</ReusableDialog>
	);
}

export default observer(ContentMetadataFieldEditor);
